/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2024-11-21 19:37:32
 * @ Description: Map marker with unique icon and attached infowindow property card
 */

import React, { useRef, useState } from 'react'

import { Marker, InfoWindow } from '@/utils/googleMapsReact'
import {
  Grid,
  Tooltip,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Box,
  IconButton,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import {
  map,
  isEmpty,
  filter,
  isNil,
  cloneDeep,
  assign,
  slice,
  sum,
  get,
  set,
  isArray,
  isObject,
  merge,
  includes,
} from '@/utils/lodash'

import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  CombinedGraphQLErrors,
  ServerError,
} from '@apollo/client'
import { ApolloProvider, useMutation } from '@apollo/client/react'
import { getMainDefinition } from '@apollo/client/utilities'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { ErrorLink } from '@apollo/client/link/error'
import { signOut } from '@/housefolios-components/SignOut'
import { SnackbarProvider } from 'notistack'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Slider from 'react-slick'
import withSession from '@/housefolios-components/Session/withSession'
import {
  ArrowDownward,
  ArrowUpward,
  Close,
  Remove,
} from '@mui/icons-material'
import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { GET_PROPERTIES } from '../queries'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'

import { GET_ME } from '@/housefolios-components/Session/queries'
import CriticalFields from '@/housefolios-components/Dialogs/CriticalFields'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
import { getProformaLink } from '@/utils/proforma'
import PropertyInformationDialogs from '@/housefolios-components/Dialogs/PropertyInformationDialogs'

function SliderArrowNext(props) {
  const { className, onClick } = props
  return (
    <div className={className} onClick={onClick}>
      <FontAwesomeIcon icon={['fas', 'chevron-right']} />
    </div>
  )
}

function SliderArrowPrev(props) {
  const { className, onClick } = props
  return (
    <div className={className} onClick={onClick}>
      <FontAwesomeIcon icon={['fas', 'chevron-left']} />
    </div>
  )
}

function CustomMapMarkerComponent(props) {
  const urlParams = new URLSearchParams(window.location.search)
  const subdomain = urlParams.get('subdomain') || null
  const subdomainParam = subdomain ? '?subdomain=' + subdomain : ''

  const { shared } = props

  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_URI,
  })
  const hostname = import.meta.env.VITE_HOSTNAME

  const wsLink = new GraphQLWsLink(
    createClient({
      url: import.meta.env.VITE_WSURI,
      options: {
        reconnect: true,
      },
    }),
  )

  const terminatingLink = ApolloLink.split(
    ({ query  }) => {
      const { kind, operation } = getMainDefinition(query)
      return (
        kind === 'OperationDefinition' && operation === 'subscription'
      )
    },
    wsLink,
    httpLink,
  )

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => {
      const token = localStorage.getItem('token')
      if (token) {
        headers = { ...headers, 'x-token': token }
      }

      return { headers }
    })

    return forward(operation)
  })

  const errorLink = new ErrorLink(({ error  }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message  }) => {
        const expired = includes(message, 'Your session expired')

        if (message === 'UNAUTHENTICATED' || expired) {
          signOut(client)
        }
      })
    } else if (ServerError.is(error) && error.statusCode === 401) {
      signOut(client)
    }
  })

  const link = ApolloLink.from([authLink, errorLink, terminatingLink])

  const cache = new InMemoryCache()

  const client = new ApolloClient({
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
  })

  const { showInfoWindow, setShowInfoWindow } = props
  const closeWindow = (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    setShowInfoWindow(null)
  }

  const { property, marketplace, propertySearch } = props

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const percentageFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })

  const slider = useRef()
  const widgetsCarousels1B = {
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    adaptiveHeight: true,
    nextArrow: <SliderArrowNext />,
    prevArrow: <SliderArrowPrev />,
  }

  const analytics = !property.acquisition
    ? {}
    : !shared && props?.session?.me?.activeOrg?.member === 'RWN'
      ? [
          {
            value: property.acquisition.analysis
              ? property.acquisition.analysis.resellPrice * 0.2
              : '-',
            label: 'Down Payment',
            type: 'currency',
          },
          {
            value: property.acquisition.analysis
              ? property.acquisition.analysis.rent
              : '-',
            label: 'Est Rental Income',
            type: 'currency',
          },
          {
            value: property.acquisition.analytics
              ? property.acquisition.analytics.buyHold.cashFlow > 0
                ? property.acquisition.analytics.buyHold.cashFlow / 12
                : '-'
              : '-',
            label: 'Est. Cashflow',
            type: 'currency',
          },
        ]
      : props.propertySearch
        ? []
        : (property.strategy === 'Fix & Flip' &&
              !props.marketplace) ||
            (property.marketplaceStrategy === 'Fix & Flip' &&
              props.marketplace)
          ? [
              {
                value: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .netROITurnkey
                    : property.acquisition.analytics.fixFlip
                        .netROIFixFlip
                  : '-',
                arrow: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? 0
                    : property.acquisition.analytics.fixFlip
                        .netROIArrow
                  : 0,
                label: 'Net ROI',
              },
              {
                value: property.acquisition.analytics
                  ? property.acquisition.analytics.fixFlip
                      .saleProceeds
                  : '-',
                arrow: property.acquisition.analytics
                  ? property.acquisition.analytics.fixFlip
                      .saleProceedsArrow
                  : 0,
                label: 'Net Profit',
                type: 'currency',
              },
              {
                value: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .allInCostTurnkey > 0
                      ? property.acquisition.analytics.turnkey
                          .allInCostTurnkey
                      : '-'
                    : property.acquisition.analytics.buyHold
                          .allInCost > 0
                      ? property.acquisition.analytics.buyHold
                          .allInCost
                      : '-'
                  : '-',
                label: 'All in Cost',
                type: 'currency',
              },
              {
                value: property.acquisition.analysis
                  ? property.acquisition.analytics.fixFlip
                      .arvTotalFixFlip
                  : '-',
                label: 'ARV',
                type: 'currency',
              },
            ]
          : [
              {
                value: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .IRRTurnkey
                    : property.acquisition.analytics.buyHold.IRR
                  : '-',
                arrow: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .IRRTurnkeyArrow
                    : property.acquisition.analytics.buyHold.IRRArrow
                  : 0,
                label: 'IRR',
              },
              {
                value: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .capRateTurnkey
                    : property.acquisition.analytics.buyHold.capRate
                  : '-',
                arrow: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .capRateTurnkeyArrow
                    : property.acquisition.analytics.buyHold
                        .capRateArrow
                  : 0,
                label:
                  (property.strategy === 'Turnkey End Buyer' &&
                    !props.marketplace) ||
                  (property.marketplaceStrategy ===
                    'Turnkey End Buyer' &&
                    props.marketplace)
                    ? 'Cap Rate'
                    : 'Yield-on-Cost',
              },
              {
                value: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .cashOnCashReturnTurnkey
                    : property.acquisition.analytics.buyHold
                        .cashOnCashReturn
                  : '-',
                arrow: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .cashOnCashReturnTurnkeyArrow
                    : property.acquisition.analytics.buyHold
                        .cashOnCashReturnArrow
                  : '-',
                label: 'Cash on Cash',
              },
              {
                value: property.acquisition.analytics
                  ? (property.strategy === 'Turnkey End Buyer' &&
                      !props.marketplace) ||
                    (property.marketplaceStrategy ===
                      'Turnkey End Buyer' &&
                      props.marketplace)
                    ? property.acquisition.analytics.turnkey
                        .equityTurnkey > 0
                      ? property.acquisition.analytics.turnkey
                          .equityTurnkey
                      : '-'
                    : property.acquisition.analytics.buyHold.equity >
                        0
                      ? property.acquisition.analytics.buyHold.equity
                      : '-'
                  : '-',
                label: 'Equity',
                type: 'currency',
              },
            ]

  const upArrows = filter(analytics, (analytic) => analytic.arrow)
  const downArrows = filter(
    analytics,
    (analytic) => !analytic.arrow && !isNil(analytic.arrow),
  )
  const noArrows = filter(analytics, (analytic) =>
    isNil(analytic.arrow),
  )

  const [updateProperty] = useMutation(UPDATE_PROPERTY)

  const phpSend = (remove = false) => {
    if (remove) {
      let data = {
        _id: property._id,
        remove_property: remove,
      }

      const str_json = JSON.stringify(data)
      //SOURCE COMMENT: I wanted to replace XMLHttpRequest so I followed the example of https://developers.google.com/web/updates/2015/03/introduction-to-fetch#:~:text=fetch()%20allows%20you%20to,the%20complex%20API%20of%20XMLHttpRequest.
      fetch(
        hostname === 'localhost'
          ? 'http://marketplace.housefolios.com/api/parserDev.php'
          : //? 'http://housefolios-marketplace.test/api/parserDev.php'
            hostname === 'dev-admin.housefolios.com'
            ? 'https://marketplace.housefolios.com/api/parserDev.php'
            : 'https://marketplace.housefolios.com/api/parser.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: str_json,
        },
      )
        .then(function (response) {
          if (response.status !== 200) {
            console.log(
              'Looks like there was a problem. Status Code: ' +
                response.status,
            )
            return
          }

          // Examine the text in the response
          response.json().then(function (data) {
            console.log(data)
          })
        })
        .catch(function (err) {
          console.log('Fetch Error :-S', err)
        })

      // const request = new XMLHttpRequest()
      // request.open(
      //   'POST',
      //   'https://marketplace.housefolios.com/api/parser.php',
      //   true,
      // )
      // request.setRequestHeader('Content-type', 'application/json')
      // request.send(str_json)
    } else {
      let data = cloneDeep(property)
      let expRelist = { expRelist: true }
      let activeMP = []
      data.beds = sum(data.beds)
      data.baths = sum(data.baths)
      data.sqft = sum(data.sqft)
      if (get(data, 'acquisition.analysis')) {
        map(data.acquisition.analysis, (value, key) => {
          if (isArray(value)) {
            set(
              data,
              `acquisition.analysis.${key}`,
              sum(get(data, `acquisition.analysis.${key}`)),
            )
          }
        })
      }
      if (get(data, 'assumptions')) {
        map(data.assumptions, (value, key) => {
          if (isArray(value)) {
            set(
              data,
              `assumptions.${key}`,
              sum(get(data, `assumptions.${key}`)),
            )
          }
        })
      }
      if (get(data, 'acquisition.analytics')) {
        map(data.acquisition.analytics, (value, key) => {
          if (isArray(value)) {
            set(
              data,
              `acquisition.analytics.${key}`,
              sum(get(data, `acquisition.analytics.${key}`)),
            )
          } else if (isObject(value)) {
            map(value, (secondKey, value) => {
              if (isArray(value)) {
                set(
                  data,
                  `acquisition.analytics.${key}.${secondKey}`,
                  sum(
                    get(
                      data,
                      `acquisition.analytics.${key}.${secondKey}`,
                    ),
                  ),
                )
              }
            })
          }
        })
      }
      const { proformaType } = property.marketplaces
      const proformaLink = getProformaLink(data._id, [proformaType])

      data.proformaURL = proformaLink
      if (
        property.marketplaces &&
        !isEmpty(property.marketplaces.marketplaceName)
      ) {
        activeMP.push({
          marketplaces: {
            marketplaceName: property.marketplaces.marketplaceName,
            approvedMarketplace:
              property.marketplaces.approvedMarketplace,
            expirationDate: new Date(
              Date.now() + 7 * 24 * 60 * 60 * 1000,
            ),
            extraTags: property.marketplaces.extraTags,
            proformaType: property.marketplaces.proformaType,
          },
        })

        data = assign(data, activeMP[0], expRelist)
      }

      const str_json = JSON.stringify(data)

      //SOURCE COMMENT: I wanted to replace XMLHttpRequest so I followed the example of https://developers.google.com/web/updates/2015/03/introduction-to-fetch#:~:text=fetch()%20allows%20you%20to,the%20complex%20API%20of%20XMLHttpRequest.
      fetch(
        hostname === 'localhost'
          ? 'http://marketplace.housefolios.com/api/parserDev.php'
          : //? 'http://housefolios-marketplace.test/api/parserDev.php'
            hostname === 'dev-admin.housefolios.com'
            ? 'https://marketplace.housefolios.com/api/parserDev.php'
            : 'https://marketplace.housefolios.com/api/parser.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: str_json,
        },
      )
        .then(function (response) {
          if (response.status !== 200) {
            console.log(
              'Looks like there was a problem. Status Code: ' +
                response.status,
            )
            return
          }

          // Examine the text in the response
          response.json().then(function (data) {
            console.log(data)
          })
        })
        .catch(function (err) {
          console.log('Fetch Error :-S', err)
        })

      // const request = new XMLHttpRequest()
      // request.open(
      //   'POST',
      //   'https://marketplace.housefolios.com/api/parser.php',
      //   true,
      // )
      // request.setRequestHeader('Content-type', 'application/json')
      // request.send(str_json)
    }
  }

  const [openCriticalFields, setOpenCriticalFields] = useState(false)

  const handleOpenCriticalFields = () => {
    setOpenCriticalFields(true)
  }

  const handleCloseCriticalFields = () => {
    setOpenCriticalFields(false)
  }

  let preferences = shared
    ? cloneDeep(DEFAULT_PREFERENCES)
    : merge(
        cloneDeep(DEFAULT_PREFERENCES),
        props?.session?.me?.activeOrg?.memberPreferences,
        props?.session?.me?.activeOrg?.preferences,
      )

  // useEffect(() => {
  //   let index = findIndex(
  //     property.images,
  //     image => image.url === property.mainImage,
  //   )
  //   //slider.current.slickGoTo(index > 0 ? index : 0)
  // }, [property])

  //label={currencyFormat.format(propertySearch ? property.price : props.marketplace ? property.acquisition.analysis.resellPrice : property.acquisition.analysis.listPrice)}
  const showPropertyInfoWindow =
    showInfoWindow === property._id &&
    !props.propertyPage &&
    !props.noInfoWindow

  return (
    <>
      <Marker
        position={props.position}
        icon={props.icon}
        clusterer={props.clusterer}
        zIndex={999}
        onClick={() => setShowInfoWindow(property._id)}
      >
        {showPropertyInfoWindow && (
            <InfoWindow
              position={props.position}
              onCloseClick={closeWindow}
              options={{
                pixelOffset: new window.google.maps.Size(205, 200),
              }}
            >
              <ApolloProvider client={client}>
                <SnackbarProvider maxSnack={3}>
                  <Box sx={{ position: 'relative', width: 300 }}>
                    <IconButton
                      aria-label="Close property card"
                      size="small"
                      onClick={closeWindow}
                      onMouseDown={(event) => event.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        zIndex: 4,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        boxShadow: 1,
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  <a
                    href={
                      props.public
                        ? `/public-property-details/${property._id}/Summary${subdomainParam}`
                        : `/property-details/${property._id}/Summary`
                    }
                    onClick={
                      props.marketplace
                        ? (event) => event.preventDefault()
                        : (event) => {
                            event.preventDefault()
                            handleOpenCriticalFields()
                          }
                    }
                  >
                    <Slider ref={slider} {...widgetsCarousels1B} style={{ width: 300 }} sx={{ flexShrink: 0, width: "100%" }}>
                      {props.propertySearch &&
                      !property.marketplaces &&
                      property.images ? (
                        map(JSON.parse(property.images), (image) => (
                          <div key={image}>
                            <Card
                              style={{
                                height: 250,
                                backgroundColor: 'black',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                              sx={{ boxShadow: 'none' }}
                            >
                              <Grid
                                container
                                direction="column"
                                style={{ height: '100%' }}
                                sx={{
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid>
                                  <Box href={ props.propertySearch ? '/property-search' : props.public ? `/public-property-details/${property._id}/Summary${subdomainParam}` : `/property-details/${property._id}/Summary` } onClick={(event) => { if (props.marketplace) event.preventDefault(); else { event.preventDefault(); handleOpenCriticalFields() } }} style={{ textAlign: 'center', backgroundColor: 'transparent', borderBottomRightRadius: '0px', }} sx={{
                                    position: "relative",
                                    borderTopLeftRadius: "inherit",
                                    borderTopRightRadius: "inherit",
                                    "& .img-wrapper-overlay": {
                                      width: "100%",
                                      height: "100%",
                                      transition: "all 0.2s ease-in-out",
                                      opacity: 0,
                                      visibility: "hidden",
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      boxShadow: "inset 0 0 2.3rem 0.5rem #000",
                                      display: "flex",
                                      alignItems: "center",
                                      alignContent: "center",
                                      color: "#fff",
                                      borderTopLeftRadius: "inherit",
                                      borderTopRightRadius: "inherit",
                                      "@media (max-width: 575.98px)": {
                                        "& + img": { width: "100% !important" },
                                      },
                                      "& .overlay-btn-wrapper": {
                                        position: "relative",
                                        zIndex: 3,
                                        textAlign: "center",
                                        width: "100%",
                                      },
                                      "&::before": {
                                        zIndex: 2,
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        content: "\"\"",
                                        width: "100%",
                                        height: "100%",
                                        transition: "all 0.2s ease-in-out",
                                        background: "#000",
                                        opacity: 0.75,
                                      },
                                    },
                                    "& .img-wrapper-overlay--visible": {
                                      visibility: "visible",
                                      opacity: 1,
                                      position: "relative",
                                      zIndex: 5,
                                    },
                                    "& .card-badges": { zIndex: 5 },
                                    "a&": {
                                      display: "block",
                                      overflow: "hidden",
                                      "&:hover .img-wrapper-overlay": { opacity: 1, visibility: "visible" },
                                    },
                                  }}>
                                    <Box component="img" src={image} style={{ objectFit: 'contain' }} alt="..." sx={{ flexShrink: 0, width: "100%" }} />
                                  </Box>
                                </Grid>
                              </Grid>
                            </Card>
                          </div>
                        ))
                      ) : props.propertySearch &&
                        !property.marketplaces &&
                        property.image ? (
                        <div>
                          <Card
                            style={{
                              height: 250,
                              backgroundColor: 'black',
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                            }}
                            sx={{ boxShadow: 'none' }}
                          >
                            <Grid
                              container
                              direction="column"
                              style={{ height: '100%' }}
                              sx={{
                                justifyContent: 'center',
                              }}
                            >
                              <Grid>
                                <Box href={ props.propertySearch ? '/property-search' : props.public ? `/public-property-details/${property._id}/Summary${subdomainParam}` : `/property-details/${property._id}/Summary` } onClick={(event) => { if (props.marketplace) event.preventDefault(); else { event.preventDefault(); handleOpenCriticalFields() } }} style={{ textAlign: 'center', backgroundColor: 'transparent', }} sx={{
                                  position: "relative",
                                  borderTopLeftRadius: "inherit",
                                  borderTopRightRadius: "inherit",
                                  "& .img-wrapper-overlay": {
                                    width: "100%",
                                    height: "100%",
                                    transition: "all 0.2s ease-in-out",
                                    opacity: 0,
                                    visibility: "hidden",
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    boxShadow: "inset 0 0 2.3rem 0.5rem #000",
                                    display: "flex",
                                    alignItems: "center",
                                    alignContent: "center",
                                    color: "#fff",
                                    borderTopLeftRadius: "inherit",
                                    borderTopRightRadius: "inherit",
                                    "@media (max-width: 575.98px)": {
                                      "& + img": { width: "100% !important" },
                                    },
                                    "& .overlay-btn-wrapper": {
                                      position: "relative",
                                      zIndex: 3,
                                      textAlign: "center",
                                      width: "100%",
                                    },
                                    "&::before": {
                                      zIndex: 2,
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      content: "\"\"",
                                      width: "100%",
                                      height: "100%",
                                      transition: "all 0.2s ease-in-out",
                                      background: "#000",
                                      opacity: 0.75,
                                    },
                                  },
                                  "& .img-wrapper-overlay--visible": {
                                    visibility: "visible",
                                    opacity: 1,
                                    position: "relative",
                                    zIndex: 5,
                                  },
                                  "& .card-badges": { zIndex: 5 },
                                  "a&": {
                                    display: "block",
                                    overflow: "hidden",
                                    "&:hover .img-wrapper-overlay": { opacity: 1, visibility: "visible" },
                                  },
                                }}>
                                  <Box component="img" src={property.image} style={{ objectFit: 'contain' }} alt="..." sx={{ flexShrink: 0, width: "100%" }} />
                                </Box>
                              </Grid>
                            </Grid>
                          </Card>
                        </div>
                      ) : !isEmpty(property.images) ? (
                        map(slice(property.images, 0, 5), (image) => (
                          <div key={image.url}>
                            <Card
                              style={{
                                height: 250,
                                backgroundColor: 'black',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                              sx={{ boxShadow: 'none' }}
                            >
                              <Grid
                                container
                                direction="column"
                                style={{ height: '100%' }}
                                sx={{
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid>
                                  <Box href={ props.marketplace ? '/marketplace' : props.public ? `/public-property-details/${property._id}/Summary${subdomainParam}` : `/property-details/${property._id}/Summary` } onClick={(event) => { if (props.marketplace) event.preventDefault(); else { event.preventDefault(); handleOpenCriticalFields() } }} style={{ textAlign: 'center', backgroundColor: 'transparent', borderBottomRightRadius: '0px', }} sx={{
                                    position: "relative",
                                    borderTopLeftRadius: "inherit",
                                    borderTopRightRadius: "inherit",
                                    "& .img-wrapper-overlay": {
                                      width: "100%",
                                      height: "100%",
                                      transition: "all 0.2s ease-in-out",
                                      opacity: 0,
                                      visibility: "hidden",
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      boxShadow: "inset 0 0 2.3rem 0.5rem #000",
                                      display: "flex",
                                      alignItems: "center",
                                      alignContent: "center",
                                      color: "#fff",
                                      borderTopLeftRadius: "inherit",
                                      borderTopRightRadius: "inherit",
                                      "@media (max-width: 575.98px)": {
                                        "& + img": { width: "100% !important" },
                                      },
                                      "& .overlay-btn-wrapper": {
                                        position: "relative",
                                        zIndex: 3,
                                        textAlign: "center",
                                        width: "100%",
                                      },
                                      "&::before": {
                                        zIndex: 2,
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        content: "\"\"",
                                        width: "100%",
                                        height: "100%",
                                        transition: "all 0.2s ease-in-out",
                                        background: "#000",
                                        opacity: 0.75,
                                      },
                                    },
                                    "& .img-wrapper-overlay--visible": {
                                      visibility: "visible",
                                      opacity: 1,
                                      position: "relative",
                                      zIndex: 5,
                                    },
                                    "& .card-badges": { zIndex: 5 },
                                    "a&": {
                                      display: "block",
                                      overflow: "hidden",
                                      "&:hover .img-wrapper-overlay": { opacity: 1, visibility: "visible" },
                                    },
                                  }}>
                                    <Box style={{ backgroundImage: 'url(' + image.url_small + ')', height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center', }} sx={{ backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
                                      <Box component="img" src={image.url} style={{ objectFit: 'contain', maxHeight: 250, }} alt="..." loading="lazy" sx={{ flexShrink: 0, width: "100%" }} />
                                    </Box>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Card>
                          </div>
                        ))
                      ) : (
                        <div>
                          <Card
                            style={{
                              height: 250,
                              backgroundColor: 'black',
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                            }}
                            sx={{ boxShadow: 'none' }}
                          >
                            <Grid
                              container
                              direction="column"
                              style={{ height: '100%' }}
                              sx={{
                                justifyContent: 'center',
                              }}
                            >
                              <Grid>
                                <Box component="a" href={ props.marketplace ? '/marketplace' : props.public ? `/public-property-details/${property._id}/Summary${subdomainParam}` : `/property-details/${property._id}/Summary` } onClick={(event) => { if (props.marketplace) event.preventDefault(); else { event.preventDefault(); handleOpenCriticalFields() } }} style={{ textAlign: 'center', backgroundColor: 'transparent', }} sx={{
                                  position: "relative",
                                  borderTopLeftRadius: "inherit",
                                  borderTopRightRadius: "inherit",
                                  "& .img-wrapper-overlay": {
                                    width: "100%",
                                    height: "100%",
                                    transition: "all 0.2s ease-in-out",
                                    opacity: 0,
                                    visibility: "hidden",
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    boxShadow: "inset 0 0 2.3rem 0.5rem #000",
                                    display: "flex",
                                    alignItems: "center",
                                    alignContent: "center",
                                    color: "#fff",
                                    borderTopLeftRadius: "inherit",
                                    borderTopRightRadius: "inherit",
                                    "@media (max-width: 575.98px)": {
                                      "& + img": { width: "100% !important" },
                                    },
                                    "& .overlay-btn-wrapper": {
                                      position: "relative",
                                      zIndex: 3,
                                      textAlign: "center",
                                      width: "100%",
                                    },
                                    "&::before": {
                                      zIndex: 2,
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      content: "\"\"",
                                      width: "100%",
                                      height: "100%",
                                      transition: "all 0.2s ease-in-out",
                                      background: "#000",
                                      opacity: 0.75,
                                    },
                                  },
                                  "& .img-wrapper-overlay--visible": {
                                    visibility: "visible",
                                    opacity: 1,
                                    position: "relative",
                                    zIndex: 5,
                                  },
                                  "& .card-badges": { zIndex: 5 },
                                  "a&": {
                                    display: "block",
                                    overflow: "hidden",
                                    "&:hover .img-wrapper-overlay": { opacity: 1, visibility: "visible" },
                                  },
                                }}>
                                  <Box component="img" src="/images/default_image.png" style={{ objectFit: 'contain' }} alt="..." sx={{ flexShrink: 0, width: "100%" }} />
                                </Box>
                              </Grid>
                            </Grid>
                          </Card>
                        </div>
                      )}
                    </Slider>
                    <CardContent
                      sx={[
                        {
                          position: "relative",
                          "& > .avatar-icon-wrapper": {
                            margin: 0,
                            position: "absolute",
                            height: 120,
                            width: 120,
                            top: -60,
                            zIndex: 3,
                            "& .avatar-icon": { height: "100%", width: "100%", boxShadow: "0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)" },
                            "& img": { maxWidth: "100%", height: "auto", width: "100%" },
                            "&.avatar-icon-wrapper--sm, &--sm": { height: 80, width: 80, top: -40 },
                          },
                          "&.text-center > .avatar-icon-wrapper": {
                            left: "50%",
                            marginLeft: -60,
                            "& .avatar-icon": { margin: 0 },
                            "&.avatar-icon-wrapper--sm, &--sm": { marginLeft: -40 },
                          },
                        },
                        { px: '1.25rem', pt: 2, pb: '1.25rem' },
                      ]}
                    >
                      <Grid container direction="column">
                        <Grid>
                          <Grid
                            container
                            direction="row"
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Grid>
                              <Box
                                component="h5"

                                style={{ fontSize: 26 }}
                                sx={{ fontWeight: 700 }}
                              >
                                {currencyFormat.format(
                                  propertySearch
                                    ? property.price
                                    : props.marketplace
                                      ? property.acquisition.analysis
                                          .resellPrice
                                      : property.acquisition.analysis
                                          .listPrice,
                                )}
                              </Box>
                            </Grid>
                            <Grid>
                              <Tooltip
                                onClick={(event) => {
                                  event.preventDefault()
                                }}
                                title={
                                  <Grid container direction="column">
                                    <Grid>
                                      <Typography>
                                        Status:{' '}
                                        {property.acquisition
                                          .activeStage
                                          ? property.acquisition
                                              .activeStage
                                          : 'new'}
                                      </Typography>
                                    </Grid>
                                    <Grid>
                                      <Typography>
                                        Strategy: {property.strategy}
                                      </Typography>
                                    </Grid>
                                    {!marketplace && (
                                      <Grid>
                                        <Typography>
                                          Portfolio:{' '}
                                          {property.portfolio?.name}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {!marketplace && (
                                      <Grid>
                                        <Typography>
                                          Favorite:{' '}
                                          {property.favorite
                                            ? 'Y'
                                            : 'N'}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                }
                              >
                                <Avatar
                                  style={{
                                    color: 'black',
                                    backgroundColor: 'transparent',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={['fal', 'info-circle']}
                                  />
                                </Avatar>
                              </Tooltip>
                            </Grid>
                            {!marketplace &&
                              property.marketplaces &&
                              !isEmpty(
                                property.marketplaces
                                  .approvedMarketplace,
                              ) && (
                                <Grid style={{ paddingBottom: 5 }}>
                                  <Tooltip
                                    onClick={(event) =>
                                      event.preventDefault()
                                    }
                                    title={
                                      <Grid
                                        container
                                        direction="column"
                                      >
                                        {map(
                                          property.marketplaces
                                            .approvedMarketplace,
                                          (marketplace) => (
                                            <Grid key={marketplace}>
                                              <Typography
                                                style={
                                                  marketplace ===
                                                    'housefolios' &&
                                                  new Date(
                                                    property
                                                      .marketplaces
                                                      .expirationDate,
                                                  ).getTime() <
                                                    Date.now()
                                                    ? { color: 'red' }
                                                    : null
                                                }
                                              >
                                                {marketplace}
                                              </Typography>
                                            </Grid>
                                          ),
                                        )}
                                      </Grid>
                                    }
                                  >
                                    <Avatar
                                      style={{
                                        color: 'black',
                                        backgroundColor:
                                          'transparent',
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'sign-hanging']} style={{ display: 'block' }} sx={{ fontSize: "1.265rem !important" }} />
                                    </Avatar>
                                  </Tooltip>
                                </Grid>
                              )}
                            {!marketplace &&
                              property.marketplaces &&
                              includes(
                                property.marketplaces
                                  .approvedMarketplace,
                                'housefolios',
                              ) &&
                              new Date(
                                property.marketplaces.expirationDate,
                              ).getTime() < Date.now() && (
                                <Grid>
                                  <Chip
                                    label="Relist"
                                    style={{
                                      backgroundColor: 'red',
                                      color: 'white',
                                      fontWeight: 600,
                                    }}
                                    onClick={(event) => {
                                      event.preventDefault()
                                      updateProperty({
                                        variables: {
                                          id: property._id,
                                          propertyInput: {
                                            marketplaces: {
                                              marketplaceName:
                                                property.marketplaces
                                                  .marketplaceName,
                                              approvedMarketplace:
                                                property.marketplaces
                                                  .approvedMarketplace,
                                              expirationDate:
                                                new Date(
                                                  Date.now() +
                                                    7 *
                                                      24 *
                                                      60 *
                                                      60 *
                                                      1000,
                                                ),
                                              extraTags:
                                                property.marketplaces
                                                  .extraTags,
                                              proformaType:
                                                property.marketplaces
                                                  .proformaType,
                                            },
                                          },
                                        },
                                        refetchQueries: [
                                          {
                                            query: GET_ME,
                                          },
                                          {
                                            query: GET_PROPERTIES,
                                            variables: {
                                              portfolioId: [
                                                props.selectedPortfolio,
                                              ],
                                              limit:
                                                GET_PROPERTIES_QUERY_LIMIT,
                                            },
                                          },
                                          {
                                            query: GET_PROPERTY,
                                            variables: {
                                              id: property._id,
                                            },
                                          },
                                        ],
                                      })
                                      // phpSend()
                                    }}
                                  />
                                </Grid>
                              )}
                          </Grid>
                        </Grid>
                        <Grid>
                          <Typography sx={{ fontSize: "0.875rem !important" }}>
                            {sum(property.beds)} bd |{' '}
                            {sum(property.baths)} ba |{' '}
                            {numberFormat.format(sum(property.sqft))}{' '}
                            sqft |{' '}
                            {propertySearch && property.yearBuilt
                              ? property.yearBuilt
                              : property.year}{' '}
                            yr
                          </Typography>
                        </Grid>
                        <Grid>
                          <Tooltip
                            onClick={(event) =>
                              event.preventDefault()
                            }
                            title={`${property.address}, ${property.city}, ${property.state} ${property.zip}`}
                          >
                            <Typography variant="h5" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: 200, }} sx={[{ fontWeight: 700 }, { fontSize: "0.875rem !important" }]}>
                              {property.address}, {property.city},{' '}
                              {property.state} {property.zip}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Grid
                          container
                          direction="row"
                          sx={{
                            justifyContent: 'space-between',
                            mt: 1,
                            pb: 0.5,
                          }}
                        >
                          <Grid>
                            {!isEmpty(upArrows) && (
                              <Tooltip
                                onClick={(event) => {
                                  event.preventDefault()
                                }}
                                title={
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    style={{ padding: 0 }}
                                    sx={{
                                      justifyContent: 'space-around',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {map(upArrows, (field) => (
                                      <Grid key={field.label}>
                                        <Grid
                                          container
                                          direction="column"
                                          sx={{
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Grid size={12}>
                                            <Typography
                                              style={{
                                                whiteSpace: 'nowrap',
                                                fontSize: 13,
                                              }}
                                            >
                                              {field.label}
                                            </Typography>
                                          </Grid>
                                          <Grid size={12}>
                                            <Grid
                                              container
                                              direction="row"
                                              wrap="nowrap"
                                              sx={{
                                                justifyContent:
                                                  'center',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Grid
                                                style={{
                                                  marginLeft: 5,
                                                }}
                                              >
                                                <Typography
                                                  style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {field.value !== '-'
                                                    ? field.type ===
                                                      'currency'
                                                      ? currencyFormat.format(
                                                          field.value,
                                                        )
                                                      : percentageFormat.format(
                                                          field.value *
                                                            100,
                                                        ) + '%'
                                                    : field.value}
                                                </Typography>
                                              </Grid>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    ))}
                                  </Grid>
                                }
                              >
                                <Grid>
                                  <Avatar
                                    style={{
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <ArrowUpward
                                      sx={{ color: '#1bc943' }}
                                    />
                                  </Avatar>
                                  <Typography
                                    style={{ marginLeft: 5 }}
                                  >
                                    {upArrows.length}/
                                    {analytics.length}
                                  </Typography>
                                </Grid>
                              </Tooltip>
                            )}
                          </Grid>
                          <Grid>
                            {!isEmpty(downArrows) && (
                              <Tooltip
                                onClick={(event) => {
                                  event.preventDefault()
                                }}
                                title={
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    style={{ padding: 0 }}
                                    sx={{
                                      justifyContent: 'space-around',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {map(downArrows, (field) => (
                                      <Grid key={field.label}>
                                        <Grid
                                          container
                                          direction="column"
                                          sx={{
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Grid size={12}>
                                            <Typography
                                              style={{
                                                whiteSpace: 'nowrap',
                                                fontSize: 13,
                                              }}
                                            >
                                              {field.label}
                                            </Typography>
                                          </Grid>
                                          <Grid size={12}>
                                            <Grid
                                              container
                                              direction="row"
                                              wrap="nowrap"
                                              sx={{
                                                justifyContent:
                                                  'center',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Grid
                                                style={{
                                                  marginLeft: 5,
                                                }}
                                              >
                                                <Typography
                                                  style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {field.value !== '-'
                                                    ? field.type ===
                                                      'currency'
                                                      ? currencyFormat.format(
                                                          field.value,
                                                        )
                                                      : percentageFormat.format(
                                                          field.value *
                                                            100,
                                                        ) + '%'
                                                    : field.value}
                                                </Typography>
                                              </Grid>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    ))}
                                  </Grid>
                                }
                              >
                                <Grid>
                                  <Avatar
                                    style={{
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <ArrowDownward color="error" />
                                  </Avatar>
                                  <Typography
                                    style={{ marginLeft: 5 }}
                                  >
                                    {downArrows.length}/
                                    {analytics.length}
                                  </Typography>
                                </Grid>
                              </Tooltip>
                            )}
                          </Grid>
                          <Grid>
                            {!isEmpty(noArrows) && (
                              <Tooltip
                                onClick={(event) => {
                                  event.preventDefault()
                                }}
                                title={
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    style={{ padding: 0 }}
                                    sx={{
                                      justifyContent: 'space-around',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {map(noArrows, (field) => (
                                      <Grid key={field.label}>
                                        <Grid
                                          container
                                          direction="column"
                                          sx={{
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Grid size={12}>
                                            <Typography
                                              style={{
                                                whiteSpace: 'nowrap',
                                                fontSize: 13,
                                              }}
                                            >
                                              {field.label}
                                            </Typography>
                                          </Grid>
                                          <Grid size={12}>
                                            <Grid
                                              container
                                              direction="row"
                                              wrap="nowrap"
                                              sx={{
                                                justifyContent:
                                                  'center',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Grid
                                                style={{
                                                  marginLeft: 5,
                                                }}
                                              >
                                                <Typography
                                                  style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {field.value !== '-'
                                                    ? field.type ===
                                                      'currency'
                                                      ? currencyFormat.format(
                                                          field.value,
                                                        )
                                                      : percentageFormat.format(
                                                          field.value *
                                                            100,
                                                        ) + '%'
                                                    : field.value}
                                                </Typography>
                                              </Grid>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    ))}
                                  </Grid>
                                }
                              >
                                <Grid>
                                  <Avatar
                                    style={{
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <Remove color="primary" />
                                  </Avatar>
                                  <Typography
                                    style={{ marginLeft: 5 }}
                                  >
                                    {noArrows.length}/
                                    {analytics.length}
                                  </Typography>
                                </Grid>
                              </Tooltip>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </a>
                  </Box>
                </SnackbarProvider>
              </ApolloProvider>
            </InfoWindow>
        )}
      </Marker>
      {showPropertyInfoWindow && !shared && (
        <PropertyInformationDialogs
          open={openCriticalFields}
          onClose={handleCloseCriticalFields}
          property={property}
          shared={shared}
          url={
            props.public
              ? `/public-property-details/${property._id}/Summary${subdomainParam}`
              : `/property-details/${property._id}/Summary`
          }
        />
      )}
    </>
  )
}

export default withSession(CustomMapMarkerComponent)
